import { Observable } from 'rxjs';
import { AppError, ErrorResponse, FileResult, toAppError } from '@imagise/entities';

export interface EditorPageViewInfo {
  url: string;
  width: number;
  height: number;
  pageIndex: number;
  numPages?: number;
}

export interface ImageEditorViewPageInfo<TInfo extends EditorPageViewInfo = EditorPageViewInfo> {
  pageIndex: number;
  isActive: boolean;
  pageInfo?: TInfo;
  error?: AppError;
}

export interface EditorPagesControllerDeps<TInfo extends EditorPageViewInfo> {
  loadPage(file: FileResult, pageIndex: number): Observable<TInfo>;
  unwrapAppError(error: AppError): AppError;
  onLoadingChanged(loading: boolean): void;
  onError(error: AppError | null): void;
  /** Fired once per image as soon as the backend reports the page count. */
  onPageCount(count: number): void;
  /** The requested page arrived — feed the source preview into the store. */
  onRequestedPagePreview(info: TInfo): void;
  /** The active page arrived — the host refreshes edit options and layers. */
  onActivePageReady(): void;
}

/**
 * Owns the multi-page preview state of the editor: which pages exist, which
 * one is active, page errors and the page-count handshake with the backend.
 */
export class EditorPagesController<TInfo extends EditorPageViewInfo = EditorPageViewInfo> {
  previewPages: ImageEditorViewPageInfo<TInfo>[] = [];
  pageCount = 0;

  private imageFile: FileResult | null = null;
  private requestedPageIndex = 0;
  private pageCountEmitted = false;

  constructor(private readonly deps: EditorPagesControllerDeps<TInfo>) {}

  get hasActiveImage(): boolean {
    return this.previewPages.some(page => page.isActive && Boolean(page.pageInfo?.url));
  }

  /** Resets the page state for a new image and requests its first page. */
  reset(file: FileResult | null): void {
    this.imageFile = file;
    this.pageCount = 0;
    this.previewPages = [];
    this.pageCountEmitted = false;
    this.deps.onPageCount(0);

    if (file) {
      this.requestPage(0);
    }
  }

  requestPage(pageIndex: number): void {
    const file = this.imageFile;
    if (!file) {
      return;
    }

    this.requestedPageIndex = pageIndex;
    this.deps.onLoadingChanged(true);
    this.deps.onError(null);
    this.deps.loadPage(file, pageIndex).subscribe(
      info => this.onPreviewInfo(info, pageIndex),
      (error: ErrorResponse) => this.onPreviewError(error, pageIndex),
    );
  }

  private onPreviewInfo(info: TInfo, activePageIndex: number): void {
    if (!info) {
      throw new Error('info cannot be null');
    }

    if (!this.pageCountEmitted && info.numPages) {
      this.pageCountEmitted = true;
      this.pageCount = info.numPages;
      this.deps.onPageCount(info.numPages);
    }

    this.expandPages(info.pageIndex, activePageIndex);
    this.previewPages[info.pageIndex].pageInfo = info;

    if (info.pageIndex === this.requestedPageIndex) {
      this.deps.onRequestedPagePreview(info);
      this.deps.onLoadingChanged(false);
      this.deps.onError(null);
    }

    if (info.pageIndex === activePageIndex) {
      this.deps.onActivePageReady();
    }
  }

  private expandPages(pageIndex: number, activePageIndex: number): void {
    if (this.previewPages.length) {
      for (let index = 0; index < this.previewPages.length; index += 1) {
        this.previewPages[index].isActive = index === activePageIndex;
      }
    }

    while (this.previewPages.length <= pageIndex) {
      const currentIdx = this.previewPages.length;
      this.previewPages.push({
        pageIndex: currentIdx,
        isActive: activePageIndex === currentIdx,
      });
    }
  }

  private onPreviewError(error: ErrorResponse, pageIndex: number): void {
    if (pageIndex === this.requestedPageIndex) {
      this.deps.onLoadingChanged(false);
    }

    this.expandPages(pageIndex, pageIndex);
    const appError = this.deps.unwrapAppError(error.error ?? toAppError(error));
    this.previewPages[pageIndex].error = appError;
    this.deps.onError(appError);
  }
}
